import { FieldsTypeTask } from "@/domain/task";

export type EditingFieldRef = {
	field_id: number;
	row_id: number | null;
};

export const editingFieldKey = (fieldId: number, rowId: number | null | undefined): string =>
	`${fieldId}:${rowId ?? ""}`;

const toProtectedKeys = (editingFields: EditingFieldRef[]): Set<string> =>
	new Set(editingFields.map((field) => editingFieldKey(field.field_id, field.row_id)));

const isProtectedField = (fieldId: number, rowId: number | null, protectedKeys: Set<string>): boolean =>
	protectedKeys.has(editingFieldKey(fieldId, rowId));

const hasProtectedNested = (
	field: FieldsTypeTask,
	protectedKeys: Set<string>,
	rowId: number | null,
): boolean => {
	if (isProtectedField(field.id, rowId, protectedKeys)) {
		return true;
	}
	if (field.fields?.some((nested) => hasProtectedNested(nested, protectedKeys, rowId))) {
		return true;
	}
	if (field.type === "table") {
		return (field.values ?? []).some((row) =>
			row.cells.some((cell) => isProtectedField(cell.id, row.row_id, protectedKeys)),
		);
	}
	return false;
};

const mergeTableValues = (
	current: FieldsTypeTask["values"],
	incoming: FieldsTypeTask["values"],
	protectedKeys: Set<string>,
): FieldsTypeTask["values"] => {
	const merged = incoming.map((incomingRow) => {
		const currentRow = current.find((row) => row.row_id === incomingRow.row_id);
		if (!currentRow) {
			return incomingRow;
		}

		const cells = mergeFields(currentRow.cells, incomingRow.cells, protectedKeys, incomingRow.row_id);
		const rowHasProtectedCell = incomingRow.cells.some((cell) =>
			isProtectedField(cell.id, incomingRow.row_id, protectedKeys),
		);

		if (rowHasProtectedCell) {
			currentRow.cells = cells;
			return currentRow;
		}

		return { ...incomingRow, cells };
	});

	for (const currentRow of current) {
		const stillExists = incoming.some((row) => row.row_id === currentRow.row_id);
		if (stillExists) {
			continue;
		}
		const isEditingRow = currentRow.cells.some((cell) =>
			isProtectedField(cell.id, currentRow.row_id, protectedKeys),
		);
		if (isEditingRow) {
			merged.push(currentRow);
		}
	}

	return merged;
};

const mergeFields = (
	current: FieldsTypeTask[],
	incoming: FieldsTypeTask[],
	protectedKeys: Set<string>,
	rowId: number | null,
): FieldsTypeTask[] => {
	const merged = incoming.map((incomingField) => {
		const currentField = current.find((field) => field.id === incomingField.id);
		if (!currentField) {
			return incomingField;
		}

		const isProtected = isProtectedField(incomingField.id, rowId, protectedKeys);

		if (incomingField.fields?.length && currentField.fields) {
			const fields = mergeFields(currentField.fields, incomingField.fields, protectedKeys, rowId);
			if (isProtected) {
				currentField.fields = fields;
				return currentField;
			}
			return { ...incomingField, fields };
		}

		if (incomingField.type === "table" && incomingField.values && currentField.values) {
			const values = mergeTableValues(currentField.values, incomingField.values, protectedKeys);
			if (isProtected) {
				currentField.values = values;
				return currentField;
			}
			return { ...incomingField, values };
		}

		return isProtected ? currentField : incomingField;
	});

	for (const currentField of current) {
		const stillExists = incoming.some((field) => field.id === currentField.id);
		if (stillExists) {
			continue;
		}
		if (hasProtectedNested(currentField, protectedKeys, rowId)) {
			merged.push(currentField);
		}
	}

	return merged;
};

export const preserveEditingFields = (
	current: FieldsTypeTask[],
	incoming: FieldsTypeTask[],
	editingFields: EditingFieldRef[],
): FieldsTypeTask[] => {
	if (!editingFields.length || !current?.length || !incoming?.length) {
		return incoming;
	}

	return mergeFields(current, incoming, toProtectedKeys(editingFields), null);
};
